import React from 'react';
import { ExclamationCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { formatBytes } from '../utils/formatters';

interface DeployStorageWarningProps {
  /**
   * Size of the deployed site in bytes
   */
  siteSize?: number;
  /**
   * Allowed storage limit in bytes
   */
  storageLimit?: number;
  /**
   * Whether to show the banner (can be controlled by parent)
   */
  isVisible?: boolean;
  /**
   * Callback when user closes the banner
   */
  onClose?: () => void;
  /**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * Banner shown when a deployment fails due to LIVE_STORAGE_EXCEEDED
 * Displays current site size and plan's storage limit
 */
const DeployStorageWarning: React.FC<DeployStorageWarningProps> = ({
  siteSize,
  storageLimit,
  isVisible = true,
  onClose,
  className = '',
}) => {
  if (!isVisible) return null;

  const siteSizeStr = siteSize ? formatBytes(siteSize) : 'Unknown';
  const storageLimitStr = storageLimit ? formatBytes(storageLimit) : 'Unknown';

  return (
    <div
      className={`
        w-full bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600 dark:border-red-500
        rounded-r-lg p-4 flex items-start justify-between gap-4
        ${className}
      `}
    >
      <div className="flex items-start gap-3 flex-1">
        <ExclamationCircleOutlined className="text-red-600 dark:text-red-400 text-lg flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-red-900 dark:text-red-200 mb-1">
            Storage Limit Exceeded
          </h3>
          <p className="text-sm text-red-800 dark:text-red-300 leading-relaxed">
            Your site is <span className="font-semibold">{siteSizeStr}</span> but your plan allows{' '}
            <span className="font-semibold">{storageLimitStr}</span> of storage. Reduce the size of your
            site or upgrade your plan to continue.
          </p>
        </div>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
          aria-label="Close notification"
        >
          <CloseOutlined className="text-lg" />
        </button>
      )}
    </div>
  );
};

export default DeployStorageWarning;
